import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import ShipInfoListTable from "../components/ShipInfoListTable";
import { getErrorMessage, getPageDescription, getSuccessMessage, getUiInterface, getPageName } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import AlertOverlay from "../../../components/Alert";
import { useShipInfo } from "../../../apis/ShipInfo/ShipInfoProvider";
import Paginacao from "../../../types/paginacao";
import { ShipInfo } from "../../../types/shipinfo";
import { FaPlane, FaInfoCircle, FaBan } from "react-icons/fa";
import ShippingInfoModal, { ShippingData } from '../components/ShippingInfoModal';

const ListView: React.FC = () => {
  const { t } = useTranslation();
  const { shipInfos, apiResponse, fetchShipInfos, markShipInfoAsShipped } = useShipInfo();
  const navigate = useNavigate();
  const [alert, setAlert] = useState<{ message: string; variant: "success" | "danger" | "warning" | "info" } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const handleCloseModal = () => setShowModal(false);

  const [selectedId, setSelectedId] = useState<number | null>(null);

    const handleAirIconClick = (id: string) => {
        setSelectedId(Number(id));
        setShowModal(true);
    };

    const handleSubmit = async (data: ShippingData) => {
        if (selectedId !== null) {
            try {
                await markShipInfoAsShipped(selectedId, data);
                setAlert({ message: getSuccessMessage("editShipInfo", { id: selectedId }), variant: "success" });
                fetchShipInfos(paginacao, searchParams);
            } catch (error) {
                setAlert({ message: getErrorMessage("editShipInfoError"), variant: "danger" });
            }
        } else {
            setAlert({ message: getErrorMessage("editNoId"), variant: "warning" });
        }

        setShowModal(false); 
    };

    
    const [searchParams, setSearchParams] = useState({
        campo: "dawb",
        valor: "",
        shippedBy: "",
        destination: "",
        freightType: "",
        releasedBy: "",
        consignee: "",
        to: "",
        releaseDate: ""
    });

    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: 20,
        page: 0,
    });

    const [paginacao, setPaginacao] = React.useState({
        pageNum: 0,
        quantityPerPage: 20
    } as Paginacao);
    
   const filterOptions = [
        { value: "dawb", label: "DAWB" },
        { value: "description", label: "Description" },
        { value: "ponumber", label: "PO Number" },
        { value: "wrnumber", label: "WR Number" },
        { value: "supplier", label: "Supplier" },
        { value: "invoicenumber", label: "Invoice Number" }
    ];

   useEffect(() => {
        const newPaginacao = {
            ...paginacao, 
            pageNum: paginationModel.page,
            quantityPerPage: paginationModel.pageSize
        };
        setPaginacao(newPaginacao);
        fetchShipInfos(newPaginacao, searchParams);
    }, [paginationModel]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();

        const valor = (e.currentTarget as any)[1].value;
        const updatedSearchParams = { ...searchParams, valor };

        const filters = Object.fromEntries(
            Object.entries(updatedSearchParams)
            .filter(([_, value]) => value !== "")
        );

        fetchShipInfos(paginacao, filters);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setSearchParams(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const navItems: NavBarItem[] = [
        { label: t('shipInfo.labels.home'), route: "/", iconName: "house-door" },
        { label: t('shipInfo.labels.shipped'), route: "/Shipinfo/Shipped", iconName: "list" }
    ];


const columns: GridColDef<ShipInfo>[] = [
  {
    field: "actions",
    headerName: "",
    width: 125,
    sortable: false,
    filterable: false,
    renderCell: (params: GridRenderCellParams<ShipInfo>) => (
      <div className="d-flex justify-content-center me-2">
        <Button
          variant="link"
          size="lg"
          onClick={() => handleAirIconClick(params.row.id)}
        >
          <FaPlane className="text-info" />
        </Button>
        <Button
          variant="link"
          size="lg"
          onClick={() => handleNavigation(`/shipinfo/${params.row.id}`)}
        >
          <FaInfoCircle className="text-primary" />
        </Button>
      </div>
    ),
  },
  {
    field: "shippedBy",
    headerName: t('shipInfo.fields.carrier'),
    width: 180,
    valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
      params?.row?.shippedBy || "-",
    renderCell: (params: GridRenderCellParams<ShipInfo>) => (
      <span style={{ color: "black" }}>{params?.row?.shippedBy || "-"}</span>
    ),
  },
  {
    field: "destination",
    headerName: t('shipInfo.fields.destination'),
    width: 180,
    valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
      params?.row?.destination?.description || "-",
    renderCell: (params: GridRenderCellParams<ShipInfo>) => (
      <span style={{ color: "black" }}>{params?.row?.destination?.description || "-"}</span>
    ),
  },
  {
    field: "freightType",
    headerName: t('shipInfo.fields.status'),
    width: 180,
    valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
      params?.row?.freightType?.description || "-",
    renderCell: (params: GridRenderCellParams<ShipInfo>) => (
      <span style={{ color: "black" }}>{params?.row?.freightType?.description || "-"}</span>
    ),
  },
  {
    field: "releasedBy",
    headerName: t('shipInfo.fields.carrier'),
    width: 180,
    valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
      params?.row?.releasedBy?.fullName || "-",
    renderCell: (params: GridRenderCellParams<ShipInfo>) => (
      <span style={{ color: "black" }}>{params?.row?.releasedBy?.fullName || "-"}</span>
    ),
  },
  {
    field: "consignee",
    headerName: t('shipInfo.fields.carrier'),
    width: 300,
    valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
      params?.row?.consignee?.name || "-",
    renderCell: (params: GridRenderCellParams<ShipInfo>) => (
      <span style={{ color: "black" }}>{params?.row?.consignee?.name || "-"}</span>
    ),
  },
  {
    field: "to",
    headerName: t('shipInfo.fields.destination'),
    width: 180,
    valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
      params?.row?.to?.name || "-",
    renderCell: (params) => {
      if (!params?.row?.to?.name) return <span>-</span>;
      return <span style={{ color: "black" }}>{params.row.to.name}</span>;
    },
  },
  {
    field: "released",
    headerName: t('shipInfo.fields.shipmentDate'),
    width: 180,
    valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
      params?.row?.released ?? "-",
    valueFormatter: (params: any) => {
      const dateStr = params?.value;
      if (!dateStr) return "-";
      try {
        const dateValue = new Date(dateStr);
        return dateValue.toLocaleDateString();
      } catch {
        return "-";
      }
    },
    renderCell: (params: GridRenderCellParams<ShipInfo>) => {
      if (!params?.row?.released) return <span>-</span>;
      try {
        const dateValue = new Date(params.row.released);
        return <span style={{ color: "black" }}>{dateValue.toLocaleDateString()}</span>;
      } catch {
        return <span>-</span>;
      }
    },
  }
];


    const handleNavigation = (route: string) => {
        navigate(route);
    };

    return (
        <Container fluid style={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <ShippingInfoModal
                show={showModal}
                onClose={handleCloseModal}
                onSubmit={handleSubmit}
            />
            <NavBar items={navItems} />
            <div className="mb-2"></div>
            <h2>{getUiInterface("search")}</h2>
            <div className="mb-2"></div>
            
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}

            <div className="mb-1 p-3 bg-light rounded">
                <h5 className="mb-2">{getUiInterface("searchFor")}</h5>
                
                <Form onSubmit={handleSearch}>
                <Row className="mb-2">
                    <Col md={3}>
                        <Form.Group controlId="campo">
                            <Form.Select
                                name="campo"
                                value={searchParams.campo}
                                onChange={handleInputChange}
                            >
                                {filterOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="valor">
                            <Form.Control
                                type="text"
                                name="valor"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                        <Button variant="primary" type="submit">
                            Search
                        </Button>
                    </Col>
                </Row>
            </Form>
            </div>

            <h3 className="mb-3">{getPageDescription("releaseList")}</h3>
            <ShipInfoListTable
                rows={shipInfos || []}
                columns={columns}
                getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
                apiResponse={apiResponse} 
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
            />
        </Container>
    );
};

export default ListView;